/**
 * P8 — turn the scrubber's current stepping session into a saved `test`.
 *
 * The scrubber accumulates its answers in `WalkerState.branchSelection` (if/try
 * arm picks, keyed by the same emitter nodeIds the engine's choice bridge uses).
 * {@link sessionFromWalkerState} lifts that into the engine's UI-agnostic
 * {@link CaptureSession}; {@link captureAndSaveTest} inverts it to `when` pins,
 * snapshots the observed trace as `then` (`captureTest`), serializes the block
 * (`printTest`), and appends it to the process's own file through the WYSIWYG
 * model-write path (`mutations.addTest`). Pure of React so the round-trip + the
 * headless (agent) capture path are both unit-testable.
 */

import { captureTest, printTest, type CaptureSession, type Diagnostic, type ProcessModel, type ResolvedModel } from "@archlang/engine";
import type { WalkerState } from "../hooks/useProcessPlayback.js";
import type { ModelMutationsApi, MutationResult } from "../workbench/mutations.js";

/** Lift the scrubber's walker state into an engine capture session — the full
 *  interactive pin set: if/try arms (`branchSelection`), plus the P10 overlay
 *  answers (`select` run-sets, injected failures, injected delays). Empty maps
 *  are dropped so an unanswered dimension stays absent from the session (the
 *  under-constrained warning then fires, as for the headless path). */
export function sessionFromWalkerState(state: WalkerState): CaptureSession {
  const session: {
    -readonly [K in keyof CaptureSession]: CaptureSession[K];
  } = {};
  if (state.branchSelection.size > 0) session.branchSelection = new Map(state.branchSelection);
  if (state.selectRuns && state.selectRuns.size > 0) session.selectRuns = new Map(state.selectRuns);
  if (state.failures && state.failures.size > 0) session.failures = new Map(state.failures);
  if (state.delays && state.delays.size > 0) session.delays = new Map(state.delays);
  return session;
}

export interface SaveTestOutcome {
  /** The model-write result (`applied` / `stale` / `unknown-target` / …). */
  readonly mutation: MutationResult;
  /** Capture diagnostics — an ambiguous (under-constrained) session warns. */
  readonly diagnostics: readonly Diagnostic[];
  /** The serialized `test` block that was written. */
  readonly testText: string;
}

/**
 * Capture the current stepping session as a `test` and write it to the process's
 * file. `processId` is the stable id the span lookup resolves the file through
 * (a `#id` or a name). Returns the write result plus any capture diagnostics.
 */
export function captureAndSaveTest(args: {
  readonly process: ProcessModel;
  readonly processId: string;
  readonly model: ResolvedModel;
  readonly state: WalkerState;
  readonly name: string;
  readonly mutations: ModelMutationsApi;
}): SaveTestOutcome {
  const session = sessionFromWalkerState(args.state);
  const { test, diagnostics } = captureTest(args.process, args.model, session, { name: args.name });
  const testText = printTest(test);
  const mutation = args.mutations.addTest({ processId: args.processId, testText });
  return { mutation, diagnostics, testText };
}
